// DS: CO0.1M
import React from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import HiddenIcon from '@wix/da-ds/pkg/Icons/32x32/Hidden';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import { getIsStash } from '../../types/deviation/getters';
import { DuperbrowseItem } from '../../Duperbrowse/redux/types';
import PublishedTime from '../_partials/PublishedTime';
import WatchButton from './WatchButton';
import MoneyPromoButton from './MoneyPromoButton';
import BackButton from './BackButton';
import RootLayout from './RootLayout';
import UserInfoBlock from './UserInfoBlock';
import BackToStashButton from '../../ArtStage/_partials/BackToStashButton';
import { getIsDuperbrowseActive } from '../../Duperbrowse/redux/selectors';
import { useSelector } from 'react-redux';

import s from './MobileSubmissionDetailsHeader.scss';

export interface Props {
  currentItem?: DuperbrowseItem;
  isSticky?: boolean;
  isStuck?: boolean;
  isOwner: boolean;
  parent?: {
    url: string;
    typeId?: number;
    itemId?: number;
  };
}

const MobileSubmissionDetailsHeader: React.FC<
  React.PropsWithChildren<Props>
> = ({ currentItem, isOwner, parent, isSticky = true, isStuck }) => {
  const { t } = useTranslation();
  const publishedTime = currentItem?.publishedTime;
  // Explicit check against false since this is mounted before a deviation finishes loading and
  // isPublished would be undefined then
  const isStashItem = currentItem?.isPublished === false;
  const isDuperbrowseActive = useSelector(getIsDuperbrowseActive);
  let moneyPromo = isStashItem
    ? undefined
    : currentItem?.extended?.showStageMonetizationPromo;
  const author = currentItem?.author;

  // If this is an adoptable that's on sale or has sold, we need to remove
  // the monetization promo components. We'll do that by setting `moneyPromo` to undefined
  if (
    currentItem?.isAdoptable &&
    (!currentItem.adoptable ||
      ['onsale', 'sold', 'pending'].includes(currentItem.adoptable.status))
  ) {
    moneyPromo = undefined;
  }

  return (
    <RootLayout
      className={classnames(
        s['root'],
        isSticky && s['sticky'],
        isStuck && s['stuck'],
        moneyPromo && s['money-promo'],
        // Explicit check since isPublished could be undefined.
        isStashItem && s['stash-header']
      )}
      prefix={
        isStashItem && !isDuperbrowseActive ? (
          <BackToStashButton />
        ) : (
          parent && <BackButton />
        )
      }
      left={
        <>
          {author && (
            <UserInfoBlock user={author} publishedTime={publishedTime} />
          )}
          {!author && publishedTime && (
            <PublishedTime
              isStash={getIsStash(currentItem)}
              publishedTime={publishedTime}
              withoutText
            />
          )}
        </>
      }
      right={
        <>
          {moneyPromo && author && (
            <MoneyPromoButton
              moneyPromo={moneyPromo}
              user={author}
              relevantDeviation={currentItem}
            />
          )}
          {!moneyPromo && !isOwner && !isStashItem && author && (
            <WatchButton user={author} />
          )}
          {isStashItem && (
            <span className={s['stash-right-text']}>
              <HiddenIcon
                size={IconSize.MEDIUM}
                disableDefaultColors
                aria-hidden="true"
              />
              {t('mobileDeviationHeader.draftView')}
            </span>
          )}
        </>
      }
    />
  );
};
MobileSubmissionDetailsHeader.displayName = 'MobileSubmissionDetailsHeader';

export default MobileSubmissionDetailsHeader;
